import { useRequestURL } from 'nuxt/app'

import { NOINDEX_NOFOLLOW, type SeoImage, type SeoSiteProfile } from '@common/core-layer/composables/seo/useSeo'
import { getConfig } from '@common/core-layer/config'
import { isSafeSeoHttpUrl } from '@common/core-layer/utils/seo.utils'
import { useLocationStore } from '@common/location-layer/store/location.store'

/**
 * Client Portal SEO site profile.
 *
 * Client Portal is white-labelled per location, so every brand value comes from portal settings
 * rather than a build-time constant. The `init-location` plugin resolves those settings during SSR
 * before page setup, so this reads a populated store on the server.
 *
 * Provide it as a getter (`provideSeoSiteProfile(() => createClientPortalSeoSiteProfile())`) so the
 * profile re-resolves if portal settings arrive late — the documented SSR-miss path in
 * `useLocation`, where the store is filled on the client tick instead.
 *
 * Defaults here are deliberately conservative: `noindex,nofollow`, so any page that does not opt in
 * with an explicit policy stays out of the index.
 */

const CLIENT_PORTAL_FALLBACK_SITE_NAME = 'Client Portal'

/** Portal banner/logo art, dropped unless it is an http(s) URL a crawler can fetch. */
function resolveClientPortalOgImage(): SeoImage {
  const { branding } = useLocationStore()
  const candidates = [branding?.bannerImage, branding?.portalBannerImage, branding?.portalImage, branding?.logo]

  const alt = branding?.portalBannerText?.trim() || CLIENT_PORTAL_FALLBACK_SITE_NAME

  for (const candidate of candidates) {
    const url = candidate?.trim()
    if (url && isSafeSeoHttpUrl(url)) {
      return { url, alt }
    }
  }

  const bucket = getConfig().CLIENT_PORTAL_STORAGE_BUCKET?.trim()
  const bucketName = bucket || 'revex-client-portal-production'
  return {
    url: `https://storage.googleapis.com/${bucketName}/assets/force-app-update-bg.png`,
    alt,
  }
}

/**
 * Canonical origin for this portal: the tenant's own custom domain when configured, else the
 * origin the request actually arrived on. Never a platform-wide constant — two locations must not
 * canonicalize to the same host.
 */
function resolveClientPortalBaseUrl(): string {
  const customDomain = useLocationStore().customDomain?.trim()
  if (customDomain) {
    const host = customDomain.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
    if (host) return `https://${host}`
  }

  try {
    const origin = useRequestURL()?.origin
    if (origin && origin !== 'null') return origin.replace(/\/+$/, '')
  } catch {
    // Outside a Nuxt request context (client navigation, tests).
  }

  const browserOrigin = globalThis.location?.origin
  if (browserOrigin && browserOrigin !== 'null') return browserOrigin.replace(/\/+$/, '')

  return ''
}

function resolveClientPortalSiteName(): string {
  const store = useLocationStore()
  const name = store.branding?.portalBannerText?.trim() || store.locationData?.portalBannerText?.trim()
  return name || CLIENT_PORTAL_FALLBACK_SITE_NAME
}

function resolveClientPortalDescription(): string {
  const store = useLocationStore()
  const description = store.branding?.portalDescription?.trim() || store.locationData?.portalDescription?.trim()
  return description || resolveClientPortalSiteName()
}

export function createClientPortalSeoSiteProfile(): SeoSiteProfile {
  const siteName = resolveClientPortalSiteName()

  return {
    siteName,
    baseUrl: resolveClientPortalBaseUrl(),
    defaultDescription: resolveClientPortalDescription(),
    defaultImage: resolveClientPortalOgImage(),
    // Opt-in indexing: only pages with an explicit policy (e.g. community about) may index.
    defaultRobots: NOINDEX_NOFOLLOW,
    defaultOgType: 'website',
    defaultTwitterCard: 'summary_large_image',
    /**
     * `{Page} | {Portal}` — the portal's own name, never "GoKollab". A community opened in a
     * Client Portal belongs to that portal's brand.
     *
     * `useBrandedHead` applies the same shape via `titleTemplate` for pages that do not call
     * `useSeo`. Pages that do call it clear that template (see `useSeo`), so the suffix is applied
     * exactly once and `og:title` matches `<title>`.
     */
    formatTitle: pageTitle => {
      const trimmed = pageTitle?.trim()
      return trimmed ? `${trimmed} | ${siteName}` : siteName
    },
  }
}
